import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { isArray } from 'lodash';
import { from } from 'rxjs';
import { ProposalFileForm } from 'src/app/classes/form-groups/proposal-file-form';
import { ProposalForm } from 'src/app/classes/form-groups/proposal-form';
import { ProposalLineItemForm } from 'src/app/classes/form-groups/proposal-line-item.form';
import { ProposalRequestVendorForm } from 'src/app/classes/form-groups/proposal-request-vendor-form';
import {
  Address,
  File as FileModel,
  ProposalRequest,
  ProposalRequestVendor,
  User
} from 'src/app/classes/models';
import { ProposalRequestLineItem } from 'src/app/classes/models/proposal-request-line-item';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { License } from 'src/app/interfaces/models/license';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-proposal',
  templateUrl: './proposal.component.html',
  styleUrls: ['./proposal.component.less'],
  providers: [
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ProposalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ProposalRequestService: ProposalRequestService,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal
  ) {
    this.ProposalRequestVendorFormGroup.addControl(
      'Proposal',
      new ProposalForm()
    );
    this.ProposalRequestVendorFormGroup.controls.Proposal!.controls.ProposalLineItems.valueChanges.subscribe(
      {
        next: () => {
          this.updateTotalPriceWithLineItems();
        }
      }
    );
  }

  CompanyId?: number;
  VendorId?: number;
  ProposalRequestId!: number;
  LoggedInUser!: User;

  ProposalRequestVendor!: ProposalRequestVendor;
  ProposalRequest!: ProposalRequest;
  ProposalRequestAddress!: Address;

  ProposalRequestVendorFormGroup = new ProposalRequestVendorForm();

  minDatePickerStartDate: NgbDateStruct = {
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  };
  minDatePickerEndDate: NgbDateStruct = {
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  };
  errors: GenericObject | null = null;
  fileErrors: GenericObject = {};

  ProposalRequestLineItemsMappedById = new Map<number, ProposalRequestLineItem>(
    []
  );
  ProposalLineItemFormsMappedByProposalRequestId = new Map<
    number,
    ProposalLineItemForm
  >([]);

  get ProposalLineItems(): FormArray<ProposalLineItemForm> {
    return this.ProposalRequestVendorFormGroup.controls.Proposal!.get(
      'ProposalLineItems'
    ) as FormArray;
  }

  isProposalRequestInFinishedState(): boolean {
    const { status } = this.ProposalRequest;
    if (!status) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Proposal Request is missing status.',
        { returnError: true }
      );
    }

    const { UserAccountType: { name } = {} } = this.LoggedInUser;

    if (name === 'Client') {
      return new Set(['AWARDED', 'CANCELLED']).has(status);
    } else {
      return new Set(['CLOSED', 'AWARDED', 'CANCELLED']).has(status);
    }
  }

  checkToDisableFormControls() {
    if (this.isProposalRequestInFinishedState()) {
      const ProposalControls: [
        'id',
        'ProposalRequestVendorId',
        'workStartDate',
        'workFinishDate',
        'totalPrice',
        'comments'
      ] = [
        'id',
        'ProposalRequestVendorId',
        'workStartDate',
        'workFinishDate',
        'totalPrice',
        'comments'
      ];

      ProposalControls.forEach((controlName) => {
        this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
          controlName
        ].disable();
      });

      this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
        'ProposalLineItems'
      ].controls.forEach((ProposalLineItemControl) => {
        ProposalLineItemControl.disable();
      });
    }
  }

  startDateChanged() {
    const startDate =
      this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
        'workStartDate'
      ].value;
    if (startDate) {
      Object.assign(this.minDatePickerEndDate, {
        day: startDate.getDate(),
        month: startDate.getMonth() + 1,
        year: startDate.getFullYear()
      });
    }
  }

  getFilesFromProposalFormGroup() {
    const Files: FileModel[] = [];
    this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
      'ProposalFiles'
    ].value.forEach((ProposalFile) => {
      const { isArchived, File } = ProposalFile;
      if (!File) {
        throw new Error('Proposal File is missing File.');
      }
      if (!isArchived) {
        Files.push(File);
      }
    });
    return Files;
  }

  fileUpload({ data: File }: { data: File }) {
    const { name: filename, size: filesize, type: filetype } = File;

    if ((Number(filesize) || 0) > 10 * 1000000) {
      this.fileErrors['isFileTooBig'] = true;
      throw new Error('Cannot add file. File is greater than 10 MB.');
    }

    const totalFileSize = this.ProposalRequestVendorFormGroup.controls[
      'Proposal'
    ]!.controls['ProposalFiles'].value.reduce((accum, prf) => {
      const { File: { filesize } = {} } = prf;

      return accum + Number(filesize);
    }, 0);

    if (totalFileSize + Number(filesize) > 25 * 1000000) {
      this.fileErrors['isFileTooBig'] = true;
      throw new Error('Cannot add file. Files total more than 25 MB.');
    }

    delete this.fileErrors['isFileTooBig'];

    return from(File.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const ProposalFilesFormArray =
          this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
            'ProposalFiles'
          ];
        const ProposalFileFormGroup = new ProposalFileForm();
        ProposalFileFormGroup.patchValue({
          File: {
            filename,
            filetype,
            filesize,
            FileContent: {
              filecontents: `data:${filetype};base64,${this.UtilityService.convertArrayBufferToBase64EncodedString(FileArrayBuffer)}`
            }
          }
        });

        ProposalFilesFormArray.push(ProposalFileFormGroup);
        this._cdr.markForCheck();
      }
    });
  }

  removeProposalRequestFile(Event: {
    File: FileModel | License;
    index: number;
  }) {
    const { index } = Event;
    const ProposalFilesFormArray =
      this.ProposalRequestVendorFormGroup.controls['Proposal']!.controls[
        'ProposalFiles'
      ];
    const ProposalFileFormGroup = ProposalFilesFormArray.at(index);
    if (ProposalFileFormGroup) {
      const ProposalFile = ProposalFileFormGroup.value;

      // If the Proposal File already exists in database, mark it as archived.
      // Otherwise, remove it before adding to Database.
      if (ProposalFile.id) {
        ProposalFileFormGroup.controls['isArchived'].setValue(true);
      } else {
        ProposalFilesFormArray.removeAt(index);
      }
      this._cdr.markForCheck();
    }
  }

  castToFileModel(value: any): FileModel {
    return value as FileModel;
  }

  handleFileDownload(File: FileModel) {
    const { filename, filetype, FileContent } = File;

    if (!filename) {
      throw this.ToastService.addErrorToast('Error', 'File is missing name', {
        returnError: true
      });
    }

    if (!FileContent || !FileContent.filecontents) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Could not find File data.',
        { returnError: true }
      );
    }

    const { filecontents } = FileContent;

    saveAs(`data:${filetype};base64,${filecontents}`, filename);
  }

  downloadFile(File: FileModel, type: 'ProposalRequestFile' | 'ProposalFile') {
    const { id: FileId } = File;
    if (!FileId) {
      throw this.ToastService.addErrorToast('Error', 'File is missing id.', {
        returnError: true
      });
    }

    if (type === 'ProposalRequestFile') {
      this.ProposalRequestService.getProposalRequestVendor({
        attributes: JSON.stringify(['id']),
        ProposalRequest: JSON.stringify({
          options: { required: true, duplicating: false },
          attributes: ['id'],
          ProposalRequestFile: {
            options: { required: true, duplicating: false },
            attributes: ['id'],
            File: {
              id: FileId,
              options: { required: true, duplicating: false }
            }
          }
        })
      }).subscribe({
        next: (ProposalRequestVendor) => {
          if (!ProposalRequestVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor information for File download.',
              { returnError: true }
            );
          }

          const { ProposalRequest } = ProposalRequestVendor;

          if (!ProposalRequest) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal information for File download.',
              { returnError: true }
            );
          }

          const { id: ProposalRequestId, ProposalRequestFiles } =
            ProposalRequest;

          if (!ProposalRequestId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find proposal request id.',
              { returnError: true }
            );
          }

          if (
            !isArray(ProposalRequestFiles) ||
            ProposalRequestFiles.length !== 1
          ) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal File information.',
              { returnError: true }
            );
          }

          const { File } = ProposalRequestFiles[0];

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find File information.',
              { returnError: true }
            );
          }

          const { filepath } = File;

          if (filepath) {
            this.ProposalRequestService.getProposalRequestFileS3PresignedUrl(
              ProposalRequestId,
              FileId
            ).subscribe({
              next: ({ presignedUrl, filename }) => {
                saveAs(presignedUrl, filename);
              }
            });
          } else {
            this.handleFileDownload(File);
          }
        }
      });
    }

    if (type === 'ProposalFile') {
      this.ProposalRequestService.getProposalRequestVendor({
        attributes: JSON.stringify(['id']),
        Proposal: JSON.stringify({
          attributes: ['id'],
          options: { required: true, duplicating: false },
          ProposalFile: {
            attributes: ['id'],
            options: { required: true, duplicating: false },
            File: {
              id: FileId,
              options: { required: true, duplicating: false }
            }
          }
        })
      }).subscribe({
        next: (ProposalRequestVendor) => {
          if (!ProposalRequestVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor information for File download.',
              { returnError: true }
            );
          }

          const { Proposal } = ProposalRequestVendor;

          if (!Proposal) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal information for File download.',
              { returnError: true }
            );
          }

          const { id: ProposalId, ProposalFiles } = Proposal;

          if (!ProposalId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find proposal id.',
              { returnError: true }
            );
          }

          if (!isArray(ProposalFiles) || ProposalFiles.length !== 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal File information.',
              { returnError: true }
            );
          }

          const ProposalFile = ProposalFiles[0];

          const { File } = ProposalFile;

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find File information.',
              { returnError: true }
            );
          }

          const { filepath } = File;

          if (filepath) {
            this.ProposalRequestService.getProposalFileS3PresignedUrl(
              ProposalId,
              FileId
            ).subscribe({
              next: ({ presignedUrl, filename }) => {
                saveAs(presignedUrl, filename);
              }
            });
          } else {
            this.handleFileDownload(File);
          }
        }
      });
    }
  }

  goToProposalRequestVendorsGrid() {
    const { UserAccountType: { name } = {} } = this.LoggedInUser;
    if (this.VendorId && name === 'Vendor') {
      this.router.navigate(['vendor', this.VendorId, 'proposals']);
    }

    if (this.CompanyId && name === 'Client') {
      this.router.navigate([
        'client',
        this.CompanyId,
        'proposal-request',
        this.ProposalRequestId,
        'proposals'
      ]);
    }
  }

  declineProposal() {
    const { result, componentInstance } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    Object.assign(componentInstance, {
      displayText: 'Are you sure you want to decline to bid?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          // The decline happens on the proposal request vendor record.
          this.ProposalRequestVendorFormGroup.removeControl('Proposal');
          this.ProposalRequestService.saveProposalRequestVendor(
            Object.assign(this.ProposalRequestVendorFormGroup.value, {
              acceptsRequest: false
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully declined to bid.'
              );
              this.goToProposalRequestVendorsGrid();
            }
          });
        }
      }
    });
  }

  submitProposal() {
    this.errors = this.UtilityService.generateValidationErrorsObject(
      this.ProposalRequestVendorFormGroup
    );
    if (this.errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with one of the fields below.',
        { returnError: true }
      );
    }

    this.updateTotalPriceWithLineItems();

    const ProposalRequestVendor = Object.assign(
      this.ProposalRequestVendorFormGroup.value,
      { acceptsRequest: true }
    );
    this.ProposalRequestService.saveProposalRequestVendor(
      ProposalRequestVendor
    ).subscribe({
      next: (ProposalRequestVendor) => {
        // this.ToastService.addSuccessToast('Success', 'Successfully submitted bid.');
        this.goToProposalRequestVendorsGrid();
      }
    });
  }

  updateTotalPriceWithLineItems() {
    const totalPrice =
      this.ProposalRequestVendorFormGroup.controls.Proposal!.controls
        .totalPrice;

    if (this.ProposalLineItems.length > 0) {
      const sumOfProposalLineItems =
        this.ProposalLineItems.getRawValue().reduce((acc, curr) => {
          const { text } = curr;

          return acc + Number(text || 0);
        }, 0);

      totalPrice.patchValue(String(sumOfProposalLineItems));
    }
  }

  navigateBack() {
    const { UserAccountType } = this.LoggedInUser;

    if (!UserAccountType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user account type information.',
        { returnError: true }
      );
    }

    const { name } = UserAccountType;

    if (!['Vendor', 'Client'].includes(name)) {
      throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
        returnError: true
      });
    }

    if (name === 'Vendor') {
      this.router.navigate(['vendor', this.VendorId, 'proposals']);
    } else {
      this.router.navigate([
        'client',
        this.CompanyId,
        'proposal-request',
        this.ProposalRequestId,
        'proposals'
      ]);
    }
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const ProposalRequestId = Number(paramMap.get('ProposalRequestId'));
    const ProposalId = Number(paramMap.get('ProposalId'));
    const VendorId = Number(paramMap.get('VendorId'));
    const CompanyId = Number(paramMap.get('CompanyId'));
    const ProposalRequestVendorId = Number(
      paramMap.get('ProposalRequestVendorId')
    );

    if (VendorId) {
      this.VendorId = VendorId;
    }

    if (CompanyId) {
      this.CompanyId = CompanyId;
    }

    if (!ProposalRequestId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Id.',
        { returnError: true }
      );
    }

    this.ProposalRequestId = ProposalRequestId;

    if (!ProposalRequestVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request Vendor Id.',
        { returnError: true }
      );
    }

    const LoggedInUser = this.AuthService.getUser();

    if (!LoggedInUser) {
      throw this.ToastService.addErrorToast('Error', 'User is not logged in.', {
        returnError: true
      });
    }

    this.LoggedInUser = LoggedInUser;

    const queryObject = {
      id: ProposalRequestVendorId,
      Proposal: JSON.stringify({
        options: { required: false },
        ProposalLineItem: {},
        ProposalFile: {
          options: { required: false },
          File: {
            options: { required: false }
          }
        }
      }),
      Address: JSON.stringify({
        options: { required: true }
      }),
      ProposalRequest: JSON.stringify({
        options: { required: true },
        id: ProposalRequestId,
        Address: {
          options: { required: true }
        },
        ProposalRequestLineItem: {},
        ProposalRequestFile: {
          options: { required: false },
          File: {
            options: { required: true }
          }
        }
      })
    };

    if (ProposalId) {
      const { Proposal } = queryObject;
      Object.assign(Proposal, { id: ProposalId });
    }

    this.ProposalRequestService.getProposalRequestVendor(queryObject).subscribe(
      {
        next: (ProposalRequestVendor) => {
          if (!ProposalRequestVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Proposal Request Vendor.',
              { returnError: true }
            );
          }

          this.ProposalRequestVendor = ProposalRequestVendor;

          this.ProposalRequestVendorFormGroup.patchValue(
            this.ProposalRequestVendor
          );

          const { ProposalRequest, Proposal } = this.ProposalRequestVendor;

          if (!ProposalRequest) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Proposal Request.',
              { returnError: true }
            );
          }

          this.ProposalRequest = ProposalRequest;

          const { Address, ProposalRequestLineItems } = this.ProposalRequest;

          if (!Address) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Proposal Request Address.',
              { returnError: true }
            );
          }

          this.ProposalRequestAddress = Address;

          if (ProposalRequestLineItems && ProposalRequestLineItems.length > 0) {
            ProposalRequestLineItems.forEach((ProposalRequestLineItem) => {
              const { id: ProposalRequestLineItemId } = ProposalRequestLineItem;

              if (!ProposalRequestLineItemId) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Proposal Request Line Item is missing Id.',
                  { returnError: true }
                );
              }

              this.ProposalRequestLineItemsMappedById.set(
                ProposalRequestLineItemId,
                ProposalRequestLineItem
              );

              if (ProposalRequestLineItemId) {
                const ProposalLineItemFormGroup = new ProposalLineItemForm();
                ProposalLineItemFormGroup.patchValue({
                  ProposalRequestLineItemId
                });

                this.ProposalLineItemFormsMappedByProposalRequestId.set(
                  ProposalRequestLineItemId,
                  ProposalLineItemFormGroup
                );
                this.ProposalLineItems.push(ProposalLineItemFormGroup);
              }
            });
          }

          if (Proposal) {
            const { ProposalFiles, ProposalLineItems } = Proposal;
            if (ProposalFiles) {
              const ProposalFilesFormArray =
                this.ProposalRequestVendorFormGroup.controls['Proposal']!
                  .controls['ProposalFiles'];
              ProposalFiles.forEach((ProposalFile) => {
                const ProposalFileFormGroup = new ProposalFileForm();
                ProposalFileFormGroup.controls['File'].removeControl(
                  'FileContent'
                );
                ProposalFileFormGroup.patchValue(ProposalFile);
                // const ProposalFileFormGroup = this.generateProposalFileFormGroup(ProposalFile);
                ProposalFilesFormArray.push(ProposalFileFormGroup);
              });
            }

            if (ProposalLineItems) {
              ProposalLineItems.forEach((lineItem) => {
                const { ProposalRequestLineItemId } = lineItem;

                if (!ProposalRequestLineItemId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing bid request line item id.',
                    { returnError: true }
                  );
                }

                const ProposalLineItemForm =
                  this.ProposalLineItemFormsMappedByProposalRequestId.get(
                    ProposalRequestLineItemId
                  );
                if (!ProposalLineItemForm) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing bid line item form.',
                    { returnError: true }
                  );
                }

                ProposalLineItemForm.patchValue(lineItem);
              });
            }
          }

          this.checkToDisableFormControls();
          this._cdr.markForCheck();
        }
      }
    );
  }
}
